import { ExpressPaymentEventMap } from '../../types/external/express-payment-component';

export const ESW_ERROR_CODES = {
  // Configuration
  MISSING_CONFIG_PARAM: 'missing_config_param',
  INVALID_SELECTOR: 'invalid_selector',

  // State
  COMPONENT_NOT_MOUNTED: 'component_not_mounted',
  MOUNT_FAILED: 'mount_failed',
  MOUNT_ABORTED: 'mount_aborted',
  COMPONENT_INITIALIZATION_TIMEOUT: 'component_initialization_timeout',
  INTEGRATION_SERVICE_UNAVAILABLE: 'integration_service_unavailable',
  FORTER_TOKEN_UNAVAILABLE: 'forter_token_unavailable',

  // Network
  NETWORK_ERROR: 'network_error',
  REQUEST_TIMEOUT: 'request_timeout',

  // API
  ADDRESS_VALIDATION_FAILED: 'address_validation_failed',
  AUTHORIZE_PAYMENT_FAILED: 'authorize_payment_failed',
  SOMETHING_WRONG: 'something_wrong',

  // Payment
  PAYMENT_DECLINED: 'payment_declined',
  PRICE_MISMATCH: 'price_mismatch',
  PAYMENT_VALIDATION_FAILED: 'payment_validation_failed',

  // Validation
  INVALID_ADDRESS: 'invalid_address',

  EVENT_HANDLER_ERROR: 'event_handler_error',
  SUBSCRIPTION_ERROR: 'subscription_error'
};

export interface ESWErrorJSON {
  type: string;
  code: string;
  message: string;
  timestamp: string;
  requestId?: string;
  fields?: Record<string, string[]>;
  statusCode?: number;
  param?: string;
}

export class ESWError extends Error {
  readonly code: string;
  readonly type: string;
  readonly timestamp: Date;
  readonly requestId?: string;
  readonly fields?: Record<string, string[]>;

  constructor(message: string, code: string, requestId?: string, type: string = 'ESWError', fields?: Record<string, string[]>) {
    super(message);
    this.code = code;
    this.requestId = requestId;
    this.timestamp = new Date();
    this.type = type;
    this.fields = fields;
  }

  toString() {
    return `${this.type}: ${this.message}`;
  }

  toJSON(): ESWErrorJSON {
    const json: ESWErrorJSON = {
      type: this.type,
      code: this.code,
      message: this.message,
      timestamp: this.timestamp.toISOString(),
      requestId: this.requestId
    };

    if (this.fields !== undefined) {
      json.fields = this.fields;
    }

    return json;
  }
}

export class ESWConfigurationError extends ESWError {
  readonly param?: string;

  constructor(message: string, code: string, param?: string) {
    super(message, code, undefined, 'ESWConfigurationError');
    this.param = param;
  }

  toJSON(): ESWErrorJSON {
    return {
      ...super.toJSON(),
      param: this.param
    };
  }
}

export class ESWValidationError extends ESWError {
  constructor(message: string, code: string, fields?: Record<string, string[]>, requestId?: string) {
    super(message, code, requestId, 'ESWValidationError', fields);
  }
}

export class ESWConnectionError extends ESWError {
  constructor(message: string, code: string, requestId?: string) {
    super(message, code, requestId, 'ESWConnectionError');
  }
}

export class ESWAPIError extends ESWError {
  readonly statusCode?: number;

  constructor(message: string, code: string, statusCode?: number, fields?: Record<string, string[]>, requestId?: string) {
    super(message, code, requestId, 'ESWAPIError', fields);
    this.statusCode = statusCode;
  }

  toJSON(): ESWErrorJSON {
    return {
      ...super.toJSON(),
      statusCode: this.statusCode
    };
  }
}

export class ESWStateError extends ESWError {
  constructor(message: string, code: string, requestId?: string) {
    super(message, code, requestId, 'ESWStateError');
  }
}

export class ESWPaymentError extends ESWError {
  constructor(message: string, code: string, fields?: Record<string, string[]>, requestId?: string) {
    super(message, code, requestId, 'ESWPaymentError', fields);
  }
}

/**
 * Type guard to check if data has an ESWError
 */
function hasESWError(data: unknown): data is { error: ESWError; component?: string } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    (data as { error: unknown }).error instanceof ESWError
  );
}

/**
 * Serializes error objects in error events to avoid minified class names
 */
export function serializeErrorEvent(data: unknown): ExpressPaymentEventMap['error'] {
  if (hasESWError(data)) {
    return {
      error: data.error.toJSON(),
      component: 'express-payment'
    };
  }

  // If already serialized or not an ESWError, return as-is
  return data as ExpressPaymentEventMap['error'];
}
